import { driver } from 'driver.js';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TourContext } from '../context/tour';

import { homeTutorial } from '../presentation/components/singleComponents/tutorial';

import api, { authHeader } from '../services/API/apiConfig';

import driverConfig from '../services/driverJS/drivers';
import * as steps from '../services/driverJS/steps';
import tourInitialState from '../services/driverJS/tourInitialState';

const TourProvider = ({ children }) => {
  const userData = JSON.parse(localStorage.getItem('token'));
  const user = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isTourOn, setTourOn] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isProgressOpen, setProgressOpen] = useState(false);
  const [tourCompletion, setTourCompletion] = useState(
    userData?.tour_status || tourInitialState
  );

  const updateStep = (tour, move) => {
    setTourCompletion((prev) => {
      let step = move === 'next' ? prev[tour].step + 1 : prev[tour].step - 1;
      if (step === -1) step = 0;

      return { ...prev, [tour]: { ...prev[tour], step } };
    });
  };

  const tourPercentage = useMemo(() => {
    const tourValues = Object.values(tourCompletion).map(
      (tour) => tour.complete
    );
    const finishedTours = tourValues.filter((v) => v);
    const percentage = (finishedTours.length * 100) / tourValues.length;
    return Math.round(percentage);
  }, [tourCompletion]);

  const driverObj = useMemo(() => {
    switch (tutorialStep) {
      case 1:
        return driver({
          ...driverConfig,
          steps: steps.onBoardingSteps,
          onNextClick: () => {
            const activeIndex = driverObj.getActiveIndex();
            if (activeIndex === 0) {
              setOpenDrawer(true);
            }
            if (activeIndex === 6) {
              setOpenDrawer(false);
            }

            updateStep('onBoarding', 'next');

            if (driverObj.isLastStep()) {
              setOpenDrawer(false);
              setTutorialStep(2);
              setTourCompletion({
                ...tourCompletion,
                onBoarding: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
            }
            setTimeout(() => driverObj.moveNext(), 300);
          },
          onPrevClick: () => {
            const activeIndex = driverObj.getActiveIndex();
            if (activeIndex === 1) {
              setOpenDrawer(false);
            }
            if (activeIndex === 7) {
              setOpenDrawer(true);
            }

            updateStep('onBoarding', 'prev');
            setTimeout(() => driverObj.movePrevious(), 300);
          },
          onCloseClick: () => {
            setOpenDrawer(false);
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setOpenDrawer(false);
            setTourOn(false);
            driverObj.destroy();
          },
        });
      case 3:
        return driver({
          ...driverConfig,
          steps: steps.homeSteps,
          onNextClick: () => {
            updateStep('home', 'next');

            if (driverObj.isLastStep()) {
              setTutorialStep(4);
              setTourCompletion({
                ...tourCompletion,
                home: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('home', 'prev');

            if (driverObj.isLastStep()) {
              setTutorialStep(2);
            }
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            driverObj.destroy();
          },
        });
      case 5:
        return driver({
          ...driverConfig,
          steps: steps.clientsSteps,
          onNextClick: () => {
            updateStep('clients', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(6);
              setTourCompletion({
                ...tourCompletion,
                clients: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('clients', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            driverObj.destroy();
          },
        });
      case 7:
        return driver({
          ...driverConfig,
          steps: steps.constructionsFormSteps,
          onNextClick: () => {
            updateStep('constructionsForm', 'next');

            const activeIndex = driverObj.getActiveIndex();
            if (activeIndex === 0) {
              navigate('/new-construction');
            }
            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(8);
              setTourCompletion({
                ...tourCompletion,
                constructionsForm: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            setTimeout(() => driverObj.moveNext(), 300);
          },
          onPrevClick: () => {
            const activeIndex = driverObj.getActiveIndex();
            if (activeIndex === 1) {
              navigate('/constructions');
            }

            updateStep('constructionsForm', 'prev');

            setTimeout(() => driverObj.movePrevious(), 300);
          },
          onCloseClick: () => {
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            driverObj.destroy();
          },
        });
      case 9:
        return driver({
          ...driverConfig,
          steps: steps.payablesSteps,
          onNextClick: () => {
            updateStep('payables', 'next');
            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(10);
              setTourCompletion({
                ...tourCompletion,
                payables: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('payables', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
        });
      case 11:
        return driver({
          ...driverConfig,
          steps: steps.budgetsSteps,
          onNextClick: () => {
            updateStep('budgets', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(12);
              setTourCompletion({
                ...tourCompletion,
                budgets: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('budgets', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
        });
      case 13:
        return driver({
          ...driverConfig,
          steps: steps.schedulesSteps,
          onNextClick: () => {
            updateStep('schedules', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(14);
              setTourCompletion({
                ...tourCompletion,
                schedules: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('schedules', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
        });
      case 15:
        return driver({
          ...driverConfig,
          steps: steps.photosSteps,
          onNextClick: () => {
            updateStep('photos', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(16);
              setTourCompletion({
                ...tourCompletion,
                photos: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('photos', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
        });
      case 17:
        return driver({
          ...driverConfig,
          steps: steps.documentsSteps,
          onNextClick: () => {
            updateStep('documents', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(18);
              setTourCompletion({
                ...tourCompletion,
                documents: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('documents', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
        });
      case 19:
        return driver({
          ...driverConfig,
          steps: steps.dashboardSteps,
          onNextClick: () => {
            updateStep('dashboard', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(20);
              setTourCompletion({
                ...tourCompletion,
                dashboard: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('dashboard', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            navigate('/admin');
            driverObj.destroy();
          },
        });
      case 21:
        return driver({
          ...driverConfig,
          steps: steps.suppliersSteps,
          onNextClick: () => {
            const activeIndex = driverObj.getActiveIndex();
            if (activeIndex === 2) {
              navigate('/new-supplier');
            }

            updateStep('suppliers', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(22);
              setTourCompletion({
                ...tourCompletion,
                suppliers: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            setTimeout(() => {
              driverObj.moveNext();
            }, 300);
          },
          onPrevClick: () => {
            const activeIndex = driverObj.getActiveIndex();
            if (activeIndex === 3) {
              navigate('/suppliers');
            }
            updateStep('suppliers', 'prev');
            setTimeout(() => {
              driverObj.movePrevious();
            }, 300);
          },
          onCloseClick: () => {
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            driverObj.destroy();
          },
        });
      case 23:
        return driver({
          ...driverConfig,
          steps: steps.categoriesSteps,
          onNextClick: () => {
            updateStep('categories', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(24);
              setTourCompletion({
                ...tourCompletion,
                categories: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('categories', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            driverObj.destroy();
          },
        });
      case 25:
        return driver({
          ...driverConfig,
          steps: steps.newReleaseSteps,
          onNextClick: () => {
            updateStep('newRelease', 'next');

            if (driverObj.isLastStep()) {
              navigate('/admin');
              setTutorialStep(26);
              setTourCompletion({
                ...tourCompletion,
                newRelease: { step: 0, complete: true },
              });
              setDialogOpen(true);
              setTourOn(false);
              driverObj.destroy();
            }
            driverObj.moveNext();
          },
          onPrevClick: () => {
            updateStep('newRelease', 'prev');
            driverObj.movePrevious();
          },
          onCloseClick: () => {
            setTourOn(false);
            driverObj.destroy();
          },
          onDestroyStarted: () => {
            setTourOn(false);
            driverObj.destroy();
          },
        });
      default:
        return null;
    }
  }, [tutorialStep, tourCompletion]);

  useEffect(() => {
    const updateTourStatus = async () => {
      if (user?.id && userData) {
        userData.tour_status = tourCompletion;
        localStorage.setItem('token', JSON.stringify(userData));
        api.patch(
          `/users/${user.id}`,
          { tour_status: tourCompletion },
          { headers: authHeader() }
        );
      }
    };

    updateTourStatus();
  }, [tourCompletion]);

  const CurrentStep = useMemo(() => homeTutorial[tutorialStep], [tutorialStep]);

  const context = useMemo(
    () => ({
      tutorialStep,
      setTutorialStep,
      isDialogOpen,
      setDialogOpen,
      openDrawer,
      setOpenDrawer,
      isProgressOpen,
      setProgressOpen,
      tourCompletion,
      setTourCompletion,
      tourPercentage,
      isTourOn,
      setTourOn,
    }),
    [
      isProgressOpen,
      isDialogOpen,
      openDrawer,
      tourCompletion,
      tourPercentage,
      tutorialStep,
      isTourOn,
    ]
  );

  return (
    <TourContext.Provider value={context}>
      {isDialogOpen && user.user_type === 'admin' && (
        <CurrentStep
          driverObj={driverObj}
          setTutorialStep={setTutorialStep}
          setProgressOpen={setProgressOpen}
          setDialogOpen={setDialogOpen}
          setTourOn={setTourOn}
          tourCompletion={tourCompletion}
          setOpenDrawer={setOpenDrawer}
        />
      )}
      {children}
    </TourContext.Provider>
  );
};

TourProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TourProvider;
